import { useCallback, useState } from "react";
import { useAuthStore, type AvailableAccount } from "@/shared/state/authstore";
import { LoginPayload, login } from "@/entities/session/api/sessionApi";
import { clearCache } from "@/queryClient";


export function useAuth() {
    const { setAuth, selectAccount: storeSelectAccount, clearAuth } = useAuthStore();
    const [isLoading, setIsLoading] = useState(false);

    const loginUser = useCallback(
        async (emailId: string, password: string, options?: { signal?: AbortSignal }): Promise<AvailableAccount[]> => {
            setIsLoading(true);

            try {
                const payload = LoginPayload.parse({
                    email: emailId,
                    password: password,
                });

                const res = await login(payload, options?.signal);

                setAuth({
                    userId: res.userId,
                    accessToken: res.accessToken,
                    refreshToken: res.refreshToken,
                    accessTokenExpiresAt: res.accessTokenExpiresAt,
                    refreshTokenExpiresAt: res.refreshTokenExpiresAt,
                    availableAccounts: res.availableAccounts,
                });

                return res.availableAccounts;
            } finally {
                setIsLoading(false);
            }
        }, [setAuth]);

    /**
     * Stores a session that was issued by something other than the password step - today, the
     * second factor. The same shape and the same store write as `loginUser`, so a two-step sign-in
     * ends in exactly the state a one-step sign-in does.
     */
    const setSession = useCallback((res: Awaited<ReturnType<typeof login>>): AvailableAccount[] => {
        setAuth({
            userId: res.userId,
            accessToken: res.accessToken,
            refreshToken: res.refreshToken,
            accessTokenExpiresAt: res.accessTokenExpiresAt,
            refreshTokenExpiresAt: res.refreshTokenExpiresAt,
            availableAccounts: res.availableAccounts,
        });

        return res.availableAccounts;
    }, [setAuth]);

    const selectAccount = useCallback(async (accountId: number) => {
        await clearCache();
        storeSelectAccount(accountId);
    }, [storeSelectAccount]);

    const logoutUser = useCallback(async () => {
        clearAuth();
        await clearCache();
    }, [clearAuth]);

    return { loginUser, setSession, logoutUser, selectAccount, isLoading };
}
